import { requestTokenRefresh } from './api'
import { clearSessionCookies, setRefreshTokenCookie } from './session-cookies'
import { getSession } from './session-store'
import { millisecondsUntilRefresh } from './tokens'
import { ApiError } from '@/api/errors'

/**
 * Renewing the access token.
 *
 * One in-flight refresh at a time, shared by everything that asks for one. Six
 * queries starting together on a page load would otherwise each notice the same
 * expiring token and send their own refresh, and with token rotation enabled five
 * of the six would be using a token the first had already spent.
 */
let inFlight: Promise<void> | null = null

export function refreshSession(token?: string): Promise<void> {
  inFlight ??= runRefresh(token).finally(() => {
    inFlight = null
  })
  return inFlight
}

export class NoSessionError extends Error {
  constructor() {
    super('There is no refresh token to renew a session with')
    this.name = 'NoSessionError'
  }
}

async function runRefresh(token?: string): Promise<void> {
  const refreshToken = token ?? getSession().refreshToken
  if (refreshToken === null || refreshToken === undefined) {
    throw new NoSessionError()
  }

  // Stamped before the request rather than after, so a slow response is counted
  // against the token's life instead of extending it. Getting this backwards
  // makes the refresh threshold drift later on every renewal.
  const receivedAt = Date.now()

  try {
    const response = await requestTokenRefresh(refreshToken)
    getSession().setSession(
      { ...response, refresh_token: response.refresh_token ?? refreshToken },
      { receivedAt },
    )
    if (response.refresh_token !== undefined) {
      setRefreshTokenCookie(response.refresh_token)
    }
    scheduleTokenRefresh()
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      endSessionLocally()
    }
    throw error
  }
}

/**
 * Ends the session in this browser without touching the identity provider's.
 *
 * For losing a session -- an expired or revoked token -- rather than for choosing
 * to leave one. Signing out at the realm as well is `signOut`, and doing it here
 * would turn every expired token into a full sign-out of every other application
 * the realm has a session with.
 */
export function endSessionLocally(): void {
  cancelScheduledRefresh()
  clearSessionCookies()
  getSession().clearSession()
}

let timer: ReturnType<typeof setTimeout> | null = null

/**
 * Renews the token on a timer as well as on demand.
 *
 * Request-driven refresh alone leaves a tab that is open but idle -- a grid
 * sitting on screen, its updates arriving over the websocket -- with a token that
 * quietly expires, so the next reconnect fails to authenticate and the view stops
 * updating with nothing on screen to say why.
 */
export function scheduleTokenRefresh(): void {
  cancelScheduledRefresh()

  const { authenticated, accessTokenPayload, tokenReceivedAt } = getSession()
  if (!authenticated || accessTokenPayload === null) return

  const delay = millisecondsUntilRefresh(accessTokenPayload, tokenReceivedAt)
  timer = setTimeout(() => {
    void refreshSession().catch(() => {
      // A 401 has already ended the session; anything else is transient and the
      // next request will try again. Either way there is nobody to tell: this
      // fired on a timer, not in response to anything the user did.
    })
  }, delay)
}

export function cancelScheduledRefresh(): void {
  if (timer !== null) {
    clearTimeout(timer)
    timer = null
  }
}

/** Test seam: whether a renewal is currently scheduled. */
export function __hasScheduledRefreshForTests(): boolean {
  return timer !== null
}
